// cell-creators.ts

import { BooleanCell, GridCell, GridCellKind } from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { ICell } from '@/core/models/columns'

// Helper function to safely convert values to string
const safeToString = (value: any): string => {
  if (Array.isArray(value)) {
    return value.join(', ') // Join arrays into a comma-separated string
  }
  return value?.toString() || '' // Ensure it returns a string, even if it's undefined
}

export const createTextCell = (cell: ICell, shouldReadOnly = false): GridCell => ({
  kind: GridCellKind.Text,
  allowOverlay: true,
  readonly: shouldReadOnly,
  data: safeToString(cell.value),
  displayData: cell.toString(),
})

const safeToNumber = (value: any): number | undefined => {
  const num = parseFloat(value)
  return isNaN(num) ? undefined : num
}

export const createDateTimeCell = (cell: ICell): GridCell =>
  ({
    kind: GridCellKind.Custom,
    allowOverlay: true,
    readonly: false,
    data: {
      type: ColumnTypes.DateTime,
      cell,
    },
    displayData: cell.toString(),
    copyData: cell.toString(),
  }) as unknown as GridCell

export const createAttachmentCell = (cell: ICell, dataEditorRef: any): GridCell => {
  const fileCell = {
    kind: GridCellKind.Custom,
    allowOverlay: true,
    readonly: false,
    data: {
      type: ColumnTypes.Attachment,
      cell,
      extra: { dataEditorRef },
    },
    copyData: cell.toString(),
    displayData: cell.toString().split('/').pop(),
    // cursor: 'pointer',
  } as unknown as GridCell

  return fileCell
}

export const createNumberCell = (cell: ICell, shouldReadOnly = false): GridCell => ({
  kind: GridCellKind.Number,
  allowOverlay: !shouldReadOnly,
  readonly: shouldReadOnly,
  data: safeToNumber(cell.value),
  displayData: cell.toString(),
  copyData: cell.value === null ? '' : String(cell.value),
})

export const createMathCell = (cell: ICell, shouldReadOnly = false): GridCell => ({
  kind: GridCellKind.Number,
  allowOverlay: !shouldReadOnly,
  readonly: shouldReadOnly,
  data: safeToNumber(cell.value),
  displayData: cell.toString(),
  copyData: cell.value === null ? '' : String(cell.value),
})

export const createUrlCell = (cell: ICell, dataEditorRef: any, shouldReadOnly = false): GridCell => {
  const urlCell = {
    kind: GridCellKind.Custom,
    allowOverlay: !shouldReadOnly,
    readonly: shouldReadOnly,
    data: {
      type: ColumnTypes.URL,
      cell,
      extra: { dataEditorRef },
    },
    cursor: 'pointer',
  } as unknown as GridCell

  return urlCell
}

export const createBooleanCell = (cell: ICell, shouldReadOnly = false): BooleanCell => ({
  kind: GridCellKind.Boolean,
  allowOverlay: false,
  readonly: shouldReadOnly,
  data: Boolean(cell.value),
})

export const createCoordinateCell = (cell: ICell, shouldReadOnly = false): GridCell => ({
  kind: GridCellKind.Text,
  allowOverlay: !shouldReadOnly,
  readonly: shouldReadOnly,
  data: cell.toString(),
  displayData: cell.toString(),
})

export const createConnectionCell = (cell: ICell): GridCell => {
  const connectionCell = {
    kind: GridCellKind.Custom,
    allowOverlay: false,
    readonly: true,
    data: {
      type: ColumnTypes.Connection,
      cell,
    },
    copyData: cell.toString(),
  } as GridCell

  return connectionCell
}

export const createLookupCell = (cell: ICell): GridCell => {
  const lookupCell = {
    kind: GridCellKind.Custom,
    allowOverlay: false,
    readonly: true,
    data: {
      type: ColumnTypes.Lookup,
      cell,
    },
    copyData: cell.toString(),
  } as GridCell

  return lookupCell
}

export const createRichTextCell = (cell: ICell, dataEditorRef: any): GridCell => {
  const richTextCell = {
    kind: GridCellKind.Custom,
    allowOverlay: true,
    readonly: false,
    data: {
      type: ColumnTypes.RichText,
      cell,
      extra: { dataEditorRef },
    },
    cursor: 'pointer',
  } as unknown as GridCell

  return richTextCell
}

export const createTemplateCell = (cell: ICell): GridCell => {
  const templateCell = {
    kind: GridCellKind.Text,
    allowOverlay: true,
    readonly: true,
    data: safeToString(cell.value),
    displayData: cell.toString(),
  } as GridCell

  return templateCell
}

export const createCustomCell = (
  cell: ICell,
  columnType: ColumnTypes,
  themeMode: string,
  shouldReadOnly = false,
  dataEditorRef: any
): GridCell => {
  const isReadOnly =
    shouldReadOnly ||
    columnType === ColumnTypes.QRCode ||
    columnType === ColumnTypes.Barcode ||
    columnType === ColumnTypes.Slug ||
    (columnType === ColumnTypes.Image && cell.settings?.imgSourceType === 'youtube')

  const extra = {
    theme: themeMode,
    dataEditorRef,
  }

  return {
    kind: GridCellKind.Custom,
    allowOverlay: !isReadOnly,
    readonly: isReadOnly,
    data: {
      type: columnType,
      cell: cell,
      extra,
      readOnly: isReadOnly,
    },
    displayData: cell?.toString() ?? '',
    copyData: cell?.toString(),
  } as GridCell
}
